import React, { useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import {
  PortcoModalShell,
  ModalBody,
  FormGrid,
  FormField,
  FieldLabel,
  TextInput,
  ContextStrip,
  ErrorBanner,
  ModalFoot,
  PrimaryBtn,
  SecondaryBtn,
} from './PortcoModalShell';

import { DateField } from '../shared/DateField';

export type LifecycleAction = 'DRAW' | 'REPAY';

interface DebtLite {
  id: number;
  instrumentType?: string;
  lender?: string;
  syntheticCusip?: string;
  drawn?: number;
  undrawn?: number;
  currency?: string;
}

interface Props {
  action: LifecycleAction;
  debt: DebtLite;
  onClose: () => void;
  onSuccess: () => void;
}

const formatMoney = (v?: number) =>
  v == null ? '—' : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Replaces window.prompt() draw / repay flows with a proper institutional
 * modal. Both POST endpoints already exist on PortcoController; this is purely
 * a UX upgrade per feedback_institutional_ux_standard.
 */
const PortcoDebtLifecycleModal: React.FC<Props> = ({ action, debt, onClose, onSuccess }) => {
  const today = getTodayDate();
  const [amount, setAmount] = useState<string>('');
  const [activityDate, setActivityDate] = useState<string>(today);
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cap = action === 'DRAW' ? debt.undrawn ?? 0 : debt.drawn ?? 0;
  const currency = debt.currency || 'USD';

  const submit = async () => {
    setError(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError(`${action === 'DRAW' ? 'Draw' : 'Repay'} amount must be greater than 0.`);
      return;
    }
    if (cap > 0 && amt > cap) {
      setError(
        action === 'DRAW'
          ? `Draw exceeds undrawn capacity (${formatMoney(cap)} ${currency}).`
          : `Repay exceeds outstanding drawn balance (${formatMoney(cap)} ${currency}).`,
      );
      return;
    }
    setSubmitting(true);
    try {
      const url = action === 'DRAW'
        ? `/api/portco/debt/${debt.id}/draw`
        : `/api/portco/debt/${debt.id}/repay`;
      const dateField = action === 'DRAW' ? 'drawDate' : 'repayDate';
      await axios.post(url, {
        amount: amt,
        [dateField]: activityDate,
        sourceRef: `manual-${action.toLowerCase()}-${Date.now()}`,
        notes: note || undefined,
      });
      onSuccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || `${action} failed.`);
    } finally {
      setSubmitting(false);
    }
  };

  const facilityLabel = `${debt.lender || 'Facility'} • ${debt.instrumentType || 'DEBT'} • ${
    debt.syntheticCusip || `#${debt.id}`
  }`;

  return (
    <PortcoModalShell
      title={action === 'DRAW' ? 'Draw Facility' : 'Repay Facility'}
      subtitle={facilityLabel}
      onClose={onClose}
    >
      <ModalBody>
        <ContextStrip>
          <span>Drawn: {formatMoney(debt.drawn)} {currency}</span>
          <span>Undrawn: {formatMoney(debt.undrawn)} {currency}</span>
        </ContextStrip>

        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <FormGrid $cols={2}>
          <FormField>
            <FieldLabel>{action === 'DRAW' ? 'Draw Amount' : 'Repay Amount'}</FieldLabel>
            <TextInput
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FormField>
          <FormField>
            <FieldLabel>{action === 'DRAW' ? 'Draw Date' : 'Repay Date'}</FieldLabel>
            <DateField
              value={activityDate ?? ''}
              onChange={iso => setActivityDate(iso)}
            />
          </FormField>
          <FormField $span={2}>
            <FieldLabel>Internal Note (optional)</FieldLabel>
            <TextInput
              type="text"
              placeholder="e.g. Q4 working capital draw"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFoot>
        <SecondaryBtn type="button" onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryBtn>
        <PrimaryBtn type="button" onClick={submit} disabled={submitting || !amount}>
          {submitting ? 'Submitting…' : action === 'DRAW' ? 'Submit Draw' : 'Submit Repay'}
        </PrimaryBtn>
      </ModalFoot>
    </PortcoModalShell>
  );
};

export default PortcoDebtLifecycleModal;
